import { css } from "@emotion/react";
import { useForm } from "@tanstack/react-form";
import {
	ChevronLeft,
	CopyIcon,
	MessageCircleQuestionMark,
	Printer,
	Share,
} from "lucide-react";
import { enqueueSnackbar } from "notistack";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
	BlueskyIcon,
	BlueskyShareButton,
	EmailIcon,
	EmailShareButton,
	FacebookIcon,
	FacebookShareButton,
	PinterestIcon,
	PinterestShareButton,
	RedditIcon,
	RedditShareButton,
	TelegramIcon,
	TelegramShareButton,
	TwitterShareButton,
	VKIcon,
	VKShareButton,
	WhatsappIcon,
	WhatsappShareButton,
} from "react-share";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { storefrontSdk } from "@/storefrontsdk";
import { useAppConfigState } from "../../hooks/useAppConfigState";
import { useCheckoutFormState } from "../../hooks/useCheckoutFormState";
import { useProductDetailsState } from "../../hooks/useProductDetailsState";
import { useSessionState } from "../../hooks/useSessionState";
import { useSiteLayoutState } from "../../hooks/useSiteLayoutState";
import { useTailwindMediaQueries } from "../../hooks/useTailwindMediaQueries";
import { useTranslate } from "../../hooks/useTranslate/useTranslate";
import { Field, FieldError, FieldGroup, FieldLabel } from "../ui/field";
import { Textarea } from "../ui/textarea";

//------------------------------------------------------------------------------------
type IframeComponentProps = {
	sourceUri: string;
	somePayload: string;
};
//------------------------------------------------------------------------------------
const IframeComponent: React.FC<IframeComponentProps> = (props) => {
	// create a ref to store the iframe element
	const iframeRef = useRef<HTMLIFrameElement>(null);

	// define a function to post messages to the iframe
	//const postMessage = (message: string) => {
	//    if (iframeRef.current?.contentWindow?.postMessage) {
	//        iframeRef.current.contentWindow.postMessage(message, window.location.origin);
	//    }
	//};

	// use useEffect to post a message when somePayload changes
	//useEffect(() => {
	//    postMessage({ type: "TYPEA", payload: props.somePayload });
	//}, [props.somePayload]);

	// use useMemo to create the iframe element and memoize it
	const memoizedIframe = useMemo(
		() => (
			<iframe
				ref={iframeRef}
				id="printpage"
				src={props.sourceUri}
				style={{
					display: "none",
					position: "absolute",
					inset: 0,
					zIndex: 101,
					width: "100vw",
					height: "100vh",
				}}
				title="Print"
			/>
		),
		[props.sourceUri],
	);

	// return the memoized iframe element
	return memoizedIframe;
};
//------------------------------------------------------------------------------------
export type PrintPageButtonProps = {
	pageUrl: string;
	printMode: boolean;
};
export const PrintPageButton: React.FC<PrintPageButtonProps> = (props) => {
	const [loadPrintPage, setLoadPrintPage] = useState(false);
	const [isPrintPageLoaded, setIsPrintPageLoaded] = useState(false);
	const isLoading = loadPrintPage && !isPrintPageLoaded;
	const [autoPrinted, setAutoPrinted] = useState(false);
	const translate = useTranslate();

	const onPrintIframe = (): Promise<void> => {
		return Promise.resolve().then(() => {
			if (!loadPrintPage) {
				setLoadPrintPage(true);
			} else if (isPrintPageLoaded) {
				printIframe("printpage");
			}
		});
	};

	const printIframe = useCallback((id: string) => {
		// biome-ignore lint/suspicious/noExplicitAny: document.frames is missing in the TypeScript definition
		const iframe = (document as any).frames
			? // biome-ignore lint/suspicious/noExplicitAny: document.frames is missing in the TypeScript definition
				((document as any).frames[id] as any)
			: document.getElementById(id);
		if (iframe) {
			const iframeWindow = iframe.contentWindow || iframe;
			iframe.focus();
			iframeWindow.print();
		}

		return false;
	}, []);

	useEffect(() => {
		if (props.printMode) {
			const handleMessage = (event: MessageEvent) => {
				// if you are troubleshooting why this is not working, then make sure your browser url matches the url in appsettings.json for PublicBaseUrl.  Window message passing will not work cross-origin.
				//console.log(event);
				if (event.data.action === "page-loaded") {
					if (!autoPrinted) {
						setIsPrintPageLoaded(true);
						setAutoPrinted(true);
						printIframe("printpage");
					}
				}
			};

			window.addEventListener("message", handleMessage);

			return () => {
				window.removeEventListener("message", handleMessage);
			};
		}
	}, [autoPrinted, props.printMode, printIframe]);

	//const PrintIframe = useHookstateMemo<JsxElement>(() => (
	//    <iframe
	//        id="printpage"
	//        src={props.pageUrl}
	//        style={{ display: 'none' }}
	//        title="Print"
	//    />
	//), [iframeUrl]);

	return (
		<>
			{loadPrintPage && (
				<IframeComponent sourceUri={props.pageUrl} somePayload={""} />
			)}
			<Button
				className="w-37.5"
				variant="outline"
				disabled={isLoading}
				onClick={onPrintIframe}
			>
				<Printer />
				<span css={css`padding:0 .5rem;`}>
					{isLoading ? "Loading..." : translate("Print")}
				</span>
			</Button>
		</>
	);
};
//------------------------------------------------------------------------------------
export const ProductDetailsButtonBar = () => {
	const appConfigState = useAppConfigState();
	const sessionState = useSessionState();
	const siteLayoutState = useSiteLayoutState();
	const productDetailsState = useProductDetailsState();
	const product = productDetailsState.get().product;
	const shareIconSize = 48;
	const shareIconRound: boolean = true;
	const checkoutFormState = useCheckoutFormState();
	let { warehouseid, locationid, fromdate, todate } = useParams();
	warehouseid = warehouseid ?? checkoutFormState.get().warehouseIdValue;
	locationid = locationid ?? checkoutFormState.get().locationIdValue;
	fromdate = fromdate ?? checkoutFormState.get().fromDateValue;
	todate = todate ?? checkoutFormState.get().toDateValue;
	const translate = useTranslate();
	const mediaQueries = useTailwindMediaQueries();
	const navigate = useNavigate();

	// Share Dialog
	const productUrl =
		product !== undefined
			? `${appConfigState.get().appConfig?.BaseUrl}/storefront/product/${product.Id}/warehouseid/${warehouseid}/locationid/${locationid}/fromdate/${fromdate}/todate/${todate}`
			: "";
	const [shareDialogOpen, setShareDialogOpen] = useState(false);
	const closeShareDialog = () => {
		setShareDialogOpen(false);
	};
	const copyLinkToClipboard = () => {
		if (window.isSecureContext && navigator && navigator.clipboard) {
			navigator.clipboard.writeText(productUrl).then(
				() => {
					enqueueSnackbar("Copied link to the clipboard", { variant: "info" });
					closeShareDialog();
				},
				() => {
					enqueueSnackbar(
						"Unable to copy link to the clipboard.  Please copy the link manually.",
						{ variant: "error" },
					);
				},
			);
		} else {
			enqueueSnackbar(
				"Copying to clipboard is not supported because the web page is not running in a secure context.  Please copy the link manually.",
				{ variant: "error" },
			);
		}
	};

	// Inquire Dialog
	const [inquireDialogOpen, setInquireDialogOpen] = useState(false);
	const sessionEmail = sessionState.get().email ?? "";
	const initialInquireFormValues = useMemo(
		() => ({
			firstName: "",
			lastName: "",
			phone: "",
			email: sessionEmail,
			message: "",
		}),
		[sessionEmail],
	);
	const inquireForm = useForm({
		defaultValues: initialInquireFormValues,
		onSubmit: async ({ value }) => {
			try {
				if (!product) {
					enqueueSnackbar(translate("Unable to send inquiry"), {
						variant: "error",
					});
					return;
				}

				const request = {
					CatalogProductId: product.Id,
					WarehouseId: warehouseid,
					LocationId: locationid,
					FirstName: value.firstName.trim(),
					LastName: value.lastName.trim(),
					Phone: value.phone.trim(),
					Email: value.email.trim(),
					Message: value.message.trim(),
				};
				const response =
					await storefrontSdk.storefrontV1SendInquiryEmailRequestAsync(request);
				const responseData = response.data as { Success?: boolean };
				if (responseData.Success) {
					enqueueSnackbar(translate("Inquiry sent"), { variant: "success" });
				} else {
					enqueueSnackbar(translate("Unable to send inquiry"), {
						variant: "error",
					});
				}

				setInquireDialogOpen(false);
				inquireForm.reset(initialInquireFormValues);
			} catch (error) {
				await siteLayoutState.handleErrorAsync(error);
			}
		},
	});
	const closeInquireDialog = () => {
		setInquireDialogOpen(false);
		inquireForm.reset(initialInquireFormValues);
	};
	const getInquireFieldError = (errors: readonly unknown[]) => {
		return typeof errors[0] === "string" ? errors[0] : "";
	};

	// Print Product
	const printProductUrl =
		product !== undefined
			? `${appConfigState.get().appConfig?.StorefrontAppUrl}/printproduct/${product.Id}/warehouseid/${warehouseid}/locationid/${locationid}/fromdate/${fromdate}/todate/${todate}`
			: "";

	if (product) {
		return (
			<div
				css={css`
                flex: 1 1 0;
                display: grid;
                grid-template-columns: ${mediaQueries.in_xs ? "auto auto" : "auto auto auto auto"};
                & > div {
                    flex: 1 1 0;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: background-color 1s;
                    padding: .5em 0;
                    cursor: pointer;
                }
                @media print {
                    display: none;
                }
            `}
			>
				{/* Back to Catalog Button */}
				<div css={css`display:flex;align-items:center;justify-content:center;`}>
					<Button
						className="w-37.5"
						variant="outline"
						onClick={() => navigate(-1)}
					>
						<ChevronLeft />
						<span css={css`padding:0 .5rem;`}>{translate("Back")}</span>
					</Button>
				</div>

				{/* INQUIRE Button */}
				<div>
					<Dialog
						open={inquireDialogOpen}
						onOpenChange={(nextOpen) => {
							if (nextOpen) {
								setInquireDialogOpen(true);
								return;
							}
							closeInquireDialog();
						}}
					>
						<DialogContent className="sm:max-w-160">
							<form
								noValidate
								className="flex max-h-full flex-col overflow-hidden"
								onSubmit={(event) => {
									event.preventDefault();
									event.stopPropagation();
									void inquireForm.handleSubmit();
								}}
							>
								<DialogHeader className="pb-4">
									<DialogTitle>
										{`${translate("Inquire about")}: ${product.ItemName}`}
									</DialogTitle>
									<DialogDescription>
										{translate(
											"Enter your contact info and ask a question about an item.",
										)}
									</DialogDescription>
								</DialogHeader>
								<FieldGroup className="overflow-y-auto pr-1">
									<inquireForm.Field
										name="firstName"
										validators={{
											onSubmit: ({ value }) =>
												value.trim().length === 0
													? translate("First Name is required")
													: undefined,
										}}
									>
										{(field) => {
											const showError =
												field.state.meta.isTouched ||
												inquireForm.state.submissionAttempts > 0;
											const error = getInquireFieldError(
												field.state.meta.errors,
											);
											return (
												<Field>
													<FieldLabel
														htmlFor="firstname"
														className="mb-1 block text-sm"
													>
														{translate("First Name")}{" "}
														<span className="text-destructive">*</span>
													</FieldLabel>
													{showError && error.length > 0 && (
														<FieldError>{error}</FieldError>
													)}
													<Input
														id="firstname"
														required
														type="text"
														value={field.state.value}
														onBlur={field.handleBlur}
														onChange={(e) =>
															field.handleChange(e.currentTarget.value)
														}
														aria-invalid={showError && error.length > 0}
													/>
												</Field>
											);
										}}
									</inquireForm.Field>
									<inquireForm.Field
										name="lastName"
										validators={{
											onSubmit: ({ value }) =>
												value.trim().length === 0
													? translate("Last Name is required")
													: undefined,
										}}
									>
										{(field) => {
											const showError =
												field.state.meta.isTouched ||
												inquireForm.state.submissionAttempts > 0;
											const error = getInquireFieldError(
												field.state.meta.errors,
											);
											return (
												<Field>
													<FieldLabel
														htmlFor="lastname"
														className="mb-1 block text-sm"
													>
														{translate("Last Name")}{" "}
														<span className="text-destructive">*</span>
													</FieldLabel>
													{showError && error.length > 0 && (
														<FieldError>{error}</FieldError>
													)}
													<Input
														id="lastname"
														required
														type="text"
														value={field.state.value}
														onBlur={field.handleBlur}
														onChange={(e) =>
															field.handleChange(e.currentTarget.value)
														}
														aria-invalid={showError && error.length > 0}
													/>
												</Field>
											);
										}}
									</inquireForm.Field>
									<inquireForm.Field
										name="phone"
										validators={{
											onSubmit: ({ value }) =>
												value.trim().length === 0
													? translate("Phone is required")
													: undefined,
										}}
									>
										{(field) => {
											const showError =
												field.state.meta.isTouched ||
												inquireForm.state.submissionAttempts > 0;
											const error = getInquireFieldError(
												field.state.meta.errors,
											);
											return (
												<Field>
													<FieldLabel
														htmlFor="phone"
														className="mb-1 block text-sm"
													>
														{translate("Phone")}{" "}
														<span className="text-destructive">*</span>
													</FieldLabel>
													{showError && error.length > 0 && (
														<FieldError>{error}</FieldError>
													)}
													<Input
														id="phone"
														required
														type="tel"
														value={field.state.value}
														onBlur={field.handleBlur}
														onChange={(e) =>
															field.handleChange(e.currentTarget.value)
														}
														aria-invalid={showError && error.length > 0}
													/>
												</Field>
											);
										}}
									</inquireForm.Field>
									<inquireForm.Field
										name="email"
										validators={{
											onSubmit: ({ value }) => {
												const email = value.trim();
												if (email.length === 0) {
													return translate("Email is required");
												}
												if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
													return translate("Enter a valid email address");
												}
												return undefined;
											},
										}}
									>
										{(field) => {
											const showError =
												field.state.meta.isTouched ||
												inquireForm.state.submissionAttempts > 0;
											const error = getInquireFieldError(
												field.state.meta.errors,
											);
											return (
												<Field>
													<FieldLabel
														htmlFor="email"
														className="mb-1 block text-sm"
													>
														{translate("Email")}{" "}
														<span className="text-destructive">*</span>
													</FieldLabel>
													{showError && error.length > 0 && (
														<FieldError>{error}</FieldError>
													)}
													<Input
														id="email"
														required
														type="email"
														value={field.state.value}
														onBlur={field.handleBlur}
														onChange={(e) =>
															field.handleChange(e.currentTarget.value)
														}
														aria-invalid={showError && error.length > 0}
													/>
												</Field>
											);
										}}
									</inquireForm.Field>
									<inquireForm.Field
										name="message"
										validators={{
											onSubmit: ({ value }) =>
												value.trim().length === 0
													? translate("Message is required")
													: undefined,
										}}
									>
										{(field) => {
											const showError =
												field.state.meta.isTouched ||
												inquireForm.state.submissionAttempts > 0;
											const error = getInquireFieldError(
												field.state.meta.errors,
											);
											return (
												<Field className="pt-4">
													<FieldLabel
														htmlFor="message"
														className="mb-1 block text-sm"
													>
														{translate("Message")}{" "}
														<span className="text-destructive">*</span>
													</FieldLabel>
													{showError && error.length > 0 && (
														<FieldError>{error}</FieldError>
													)}
													<Textarea
														id="message"
														required
														value={field.state.value}
														onBlur={field.handleBlur}
														onChange={(e) =>
															field.handleChange(e.currentTarget.value)
														}
														aria-invalid={showError && error.length > 0}
														className="dark:bg-input/0 border-input focus-visible:border-ring focus-visible:ring-ring/50 min-h-37.5 w-full rounded-lg border bg-transparent px-2.5 py-2 text-sm outline-none focus-visible:ring-[3px]"
													/>
												</Field>
											);
										}}
									</inquireForm.Field>
								</FieldGroup>
								<DialogFooter className="mt-4 sticky bottom-0">
									<Button
										type="button"
										variant="outline"
										onClick={closeInquireDialog}
									>
										{translate("Cancel")}
									</Button>
									<Button
										type="submit"
										disabled={inquireForm.state.isSubmitting}
									>
										{translate("Send")}
									</Button>
								</DialogFooter>
							</form>
						</DialogContent>
					</Dialog>
					<Button
						className="w-37.5"
						variant="outline"
						onClick={async () => {
							return Promise.resolve().then(() => {
								setInquireDialogOpen(true);
							});
						}}
					>
						<MessageCircleQuestionMark />
						<span css={css`padding:0 .5rem;`}>{translate("Inquire")}</span>
					</Button>
				</div>

				{/* SHARE Button */}
				<div>
					<Dialog
						open={shareDialogOpen}
						onOpenChange={(nextOpen) => {
							if (nextOpen) {
								setShareDialogOpen(true);
								return;
							}
							closeShareDialog();
						}}
					>
						<DialogContent className="sm:max-w-160">
							<DialogHeader>
								<DialogTitle>{translate("Share")}</DialogTitle>
							</DialogHeader>
							<div className="flex flex-wrap gap-2">
								<Tooltip>
									<TooltipTrigger
										render={
											<EmailShareButton
												subject={`Inquiry: #${product?.InventoryNo} ${product.ItemName}`}
												url={productUrl}
											>
												<EmailIcon
													size={shareIconSize}
													round={shareIconRound}
												/>
											</EmailShareButton>
										}
									/>
									<TooltipContent>E-mail</TooltipContent>
								</Tooltip>
								<Tooltip>
									<TooltipTrigger
										render={
											<BlueskyShareButton url={productUrl}>
												<BlueskyIcon
													size={shareIconSize}
													round={shareIconRound}
												/>
											</BlueskyShareButton>
										}
									/>
									<TooltipContent>Bluesky</TooltipContent>
								</Tooltip>
								<Tooltip>
									<TooltipTrigger
										render={
											<FacebookShareButton url={productUrl}>
												<FacebookIcon
													size={shareIconSize}
													round={shareIconRound}
												/>
											</FacebookShareButton>
										}
									/>
									<TooltipContent>Facebook</TooltipContent>
								</Tooltip>
								<Tooltip>
									<TooltipTrigger
										render={
											<PinterestShareButton url={productUrl} media={productUrl}>
												<PinterestIcon
													size={shareIconSize}
													round={shareIconRound}
												/>
											</PinterestShareButton>
										}
									/>
									<TooltipContent>Pinterest</TooltipContent>
								</Tooltip>
								<Tooltip>
									<TooltipTrigger
										render={
											<RedditShareButton url={productUrl}>
												<RedditIcon
													size={shareIconSize}
													round={shareIconRound}
												/>
											</RedditShareButton>
										}
									/>
									<TooltipContent>Reddit</TooltipContent>
								</Tooltip>
								<Tooltip>
									<TooltipTrigger
										render={
											<TelegramShareButton url={productUrl}>
												<TelegramIcon
													size={shareIconSize}
													round={shareIconRound}
												/>
											</TelegramShareButton>
										}
									/>
									<TooltipContent>Telegram</TooltipContent>
								</Tooltip>
								<Tooltip>
									<TooltipTrigger
										render={
											<VKShareButton url={productUrl}>
												<VKIcon size={shareIconSize} round={shareIconRound} />
											</VKShareButton>
										}
									/>
									<TooltipContent>VK</TooltipContent>
								</Tooltip>
								<Tooltip>
									<TooltipTrigger
										render={
											<WhatsappShareButton url={productUrl}>
												<WhatsappIcon
													size={shareIconSize}
													round={shareIconRound}
												/>
											</WhatsappShareButton>
										}
									/>
									<TooltipContent>WhatsApp</TooltipContent>
								</Tooltip>
								<Tooltip>
									<TooltipTrigger
										render={
											<TwitterShareButton url={productUrl}>
												<div
													css={css`background-color:#000000;width:${shareIconSize}px;height:${shareIconSize}px;display:flex;justify-content:center;align-items:center;border-radius:50%;overflow:hidden;`}
												>
													<svg
														width={shareIconSize / 2.5}
														height={shareIconSize / 2.5}
														viewBox="0 0 1200 1227"
														fill="none"
														xmlns="http://www.w3.org/2000/svg"
													>
														<title>Twitter</title>
														<path
															d="M714.163 519.284L1160.89 0H1055.03L667.137 450.887L357.328 0H0L468.492 681.821L0 1226.37H105.866L515.491 750.218L842.672 1226.37H1200L714.137 519.284H714.163ZM569.165 687.828L521.697 619.934L144.011 79.6944H306.615L611.412 515.685L658.88 583.579L1055.08 1150.3H892.476L569.165 687.854V687.828Z"
															fill="white"
														/>
													</svg>
												</div>
											</TwitterShareButton>
										}
									/>
									<TooltipContent>X</TooltipContent>
								</Tooltip>
							</div>
							<div className="mt-3 flex items-center gap-2">
								<div className="flex-1">
									<label htmlFor="share-link" className="mb-1 block text-sm">
										{translate("Link")}
									</label>
									<Input
										id="share-link"
										readOnly
										value={productUrl}
										onFocus={(e) => {
											e.target.select();
										}}
									/>
								</div>
								<div className="pt-5">
									<Tooltip>
										<TooltipTrigger
											render={
												<Button
													variant="ghost"
													size="icon-sm"
													aria-label="copy"
													onClick={copyLinkToClipboard}
												>
													<CopyIcon />
												</Button>
											}
										/>
										<TooltipContent>{translate("Copy")}</TooltipContent>
									</Tooltip>
								</div>
							</div>
						</DialogContent>
					</Dialog>
					<Button
						className="w-37.5"
						variant="outline"
						onClick={async () => {
							return Promise.resolve().then(() => {
								setShareDialogOpen(true);
							});
						}}
					>
						<Share />
						<span css={css`padding:0 .5rem;`}>{translate("Share")}</span>
					</Button>
				</div>

				{/* PRINT Button */}
				<div>
					<PrintPageButton pageUrl={`${printProductUrl}`} printMode={true} />
				</div>

				{/* Back to Catalog Button */}
				{productDetailsState.get().showPopup && (
					<div>
						<Button
							className="w-37.5"
							variant="destructive"
							onClick={async () => {
								return Promise.resolve().then(() => {
									productDetailsState.setShowPopup(false);
								});
							}}
						>
							{/* <Close />Back to Catalog */}
							<ChevronLeft />
							Back to Catalog
						</Button>
					</div>
				)}
			</div>
		);
	}
	return null;
};
